/**
 * Dinamik rejim.
 *   batch      — quritish kinetikasi ODE: dX/dt = −N(X)·A/G_dry, RK4 (kinetics.ts).
 *   continuous — har qadamda turg'un yechim + vaqt (MODEL_GUIDE §2), evaporated = ∫W dt.
 *
 * ALGORITM (batch)
 *   1. Havo holati (T1, Y1), T_wb, N_c va k = N_c·A/G_dry qadam boshida hisoblanadi
 *      (kirishlar qadamda o'zgarmas).
 *   2. Agar X > Xc va o'zgarmas tezlik davri shu qadam ichida tugasa, kritik nuqtagacha
 *      analitik (chiziqli) o'tiladi — RK4 sinish nuqtasi ustidan o'tmaydi.
 *   3. Qolgan qismi RK4 (ichki qadam ≤ 0.2·τ, τ = (Xc − X*)/k — pasayuvchi davr vaqt doimiysi).
 *   4. X < X* ga tushmaydi (N = 0).
 *
 * Holat: {t, X, Tmaterial, evaporated}. Tmaterial = T1 − N·r/h (kvazistatsionar).
 */
import { heatOfVaporization } from '../../properties';
import { stepWithSubsteps } from '../../solvers/rk4';
import type { StepResult, Warning } from '../../types';
import { warn } from '../../types';
import { dryingRate, timeToMoisture } from './kinetics';
import { batchKinetics, solveContinuous, validateInputs, validateParams } from './steady';
import type { DryerInputs, DryerParams, DryerState, DryerStepOutputs, DryingPhase } from './types';

export function initState(p: DryerParams, i: DryerInputs): DryerState {
  validateParams(p);
  validateInputs(p, i);
  return { t: 0, X: i.X1, Tmaterial: i.T0, evaporated: 0 };
}

function stepContinuous(
  p: DryerParams,
  state: DryerState,
  i: DryerInputs,
  dt: number,
): StepResult<DryerState, DryerStepOutputs> {
  const warnings: Warning[] = [];
  const { outputs } = solveContinuous(p, i, warnings);
  const next: DryerState = {
    t: state.t + dt,
    X: outputs.X2actual,
    Tmaterial: outputs.Twb1,
    evaporated: state.evaporated + outputs.W * dt,
  };
  return { state: next, outputs: { ...outputs, t: next.t }, warnings };
}

export function stepDynamic(
  p: DryerParams,
  state: DryerState,
  i: DryerInputs,
  dt: number,
): StepResult<DryerState, DryerStepOutputs> {
  validateInputs(p, i);
  if (!(dt > 0)) throw new Error('convective-dryer: dt must be > 0');
  if (p.mode === 'continuous') return stepContinuous(p, state, i, dt);

  const warnings: Warning[] = [];
  const { h, Twb, Nc, kRate, kin, air1 } = batchKinetics(p, i, warnings);
  const A = p.dryingArea ?? 0;
  const G = i.drySolid;
  if (p.maxMaterialTemperature !== undefined && i.T1 > p.maxMaterialTemperature)
    warn(warnings, 'dryer.overheating', 'warning', {
      T1: Math.round(i.T1),
      Tmax: Math.round(p.maxMaterialTemperature),
    });
  if (i.X2 < kin.Xeq)
    warn(warnings, 'dryer.belowEquilibrium', 'warning', { X2: i.X2, Xeq: kin.Xeq });
  if (i.airFlow <= 0) warn(warnings, 'dryer.noAirflow', 'critical');
  if (!(Nc > 0) && state.X > kin.Xeq) warn(warnings, 'dryer.noDrying', 'info');

  let X = state.X;
  let evaporated = state.evaporated;
  let remaining = dt;

  // 2. O'zgarmas tezlik davri: chiziqli, analitik
  if (X > kin.Xc && kRate > 0) {
    const tCross = (X - kin.Xc) / kRate;
    if (tCross >= remaining) {
      X -= kRate * remaining;
      evaporated += G * kRate * remaining;
      remaining = 0;
    } else {
      evaporated += G * (X - kin.Xc);
      X = kin.Xc;
      remaining -= tCross;
    }
  }

  // 3. Pasayuvchi davr: RK4
  if (remaining > 0 && kRate > 0 && X > kin.Xeq) {
    const tau = (kin.Xc - kin.Xeq) / kRate;
    const deriv = (_t: number, y: readonly number[]): number[] => {
      const x = y[0] as number;
      const dX = -(dryingRate(x, kin) * A) / G;
      return [dX, -G * dX];
    };
    const y = stepWithSubsteps(
      deriv,
      state.t + dt - remaining,
      [X, evaporated],
      remaining,
      0.2 * tau,
    );
    X = Math.max(kin.Xeq, y[0] as number);
    evaporated = y[1] as number;
  }

  const N = dryingRate(X, kin);
  const r = heatOfVaporization('water', Twb);
  const Tmaterial = h > 0 ? air1.T - (N * r) / h : air1.T;
  const phase: DryingPhase = X <= i.X2 + 1e-12 ? 'done' : X > kin.Xc ? 'constant' : 'falling';
  const next: DryerState = { t: state.t + dt, X, Tmaterial, evaporated };
  return {
    state: next,
    outputs: {
      mode: 'batch',
      t: next.t,
      X,
      rate: N,
      dXdt: G > 0 ? -(N * A) / G : 0,
      Nc,
      Tmaterial,
      Twb,
      phase,
      timeToTarget: timeToMoisture(X, Math.max(i.X2, kin.Xeq), kin, kRate),
      evaporated,
    },
    warnings,
  };
}
